import React, { useEffect, useState } from "react";
import { useParams, useHistory } from "react-router-dom";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { ProgressSpinner } from "primereact/progressspinner";
import ResponsiveView from "../../components/responsive/ResponsiveView";
import DataCard from "../../components/responsive/DataCard";
import { fetchAdminShiftById, formatHours, shiftStatusBadgeClass, shiftStatusLabel } from "../../services/shiftService";
import ShiftMap from "./ShiftMap";
import styles from "./shifts.module.scss";

const staffName = (staff) => (staff ? `${staff.firstName || ""} ${staff.lastName || ""}`.trim() || staff.email : "Unknown");

const mapsLink = (latitude, longitude) => `https://www.google.com/maps?q=${latitude},${longitude}`;

const LocationCell = ({ point }) => {
    if (!point) return <span>-</span>;
    return (
        <a href={mapsLink(point.latitude, point.longitude)} target="_blank" rel="noreferrer">
            {point.latitude.toFixed(5)}, {point.longitude.toFixed(5)}
        </a>
    );
};

const AdminShiftDetail = () => {
    const { id } = useParams();
    const history = useHistory();
    const [shift, setShift] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const load = async () => {
            setLoading(true);
            const res = await fetchAdminShiftById(id);
            setShift(res?.data || null);
            setLoading(false);
        };
        load();
    }, [id]);

    if (loading) {
        return (
            <div className={styles.page}>
                <div className={styles.loadingState}>
                    <ProgressSpinner strokeWidth="4" />
                    <span>Loading shift...</span>
                </div>
            </div>
        );
    }

    if (!shift) {
        return (
            <div className={styles.page}>
                <div className={styles.pageHeader}>
                    <div className={styles.headerTitleRow}>
                        <div className={styles.headerIcon}>
                            <i className="pi pi-exclamation-triangle" />
                        </div>
                        <div>
                            <h2>Shift not found</h2>
                            <p className={styles.subtitle}>This shift may have been removed.</p>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    const geoPingBody = (row) => <LocationCell point={row} />;
    const geoTimeBody = (row) => new Date(row.time).toLocaleString();

    const clockMarkers = [
        { lat: shift.clockIn.latitude, lng: shift.clockIn.longitude, label: "In", color: "#10b981", title: `Clock In - ${new Date(shift.clockIn.time).toLocaleString()}` },
        ...(shift.clockOut
            ? [{ lat: shift.clockOut.latitude, lng: shift.clockOut.longitude, label: "Out", color: "#ef4444", title: `Clock Out - ${new Date(shift.clockOut.time).toLocaleString()}` }]
            : []),
    ];

    const pingMarkers = (shift.geoPings || []).map((p, idx) => ({
        lat: p.latitude,
        lng: p.longitude,
        label: String(idx + 1),
        color: "#6366f1",
        title: new Date(p.time).toLocaleString(),
    }));

    return (
        <div className={styles.page}>
            <div className={styles.backLink} onClick={() => history.push("/admin/shifts")}>
                <i className="pi pi-arrow-left" /> Back to Shifts
            </div>

            <div className={styles.pageHeader}>
                <div className={styles.headerTitleRow}>
                    <div className={styles.headerIcon}>
                        <i className="pi pi-clock" />
                    </div>
                    <div>
                        <h2>{staffName(shift.staff)}</h2>
                        <p className={styles.subtitle}>
                            {shift.role} &middot; {shift.staff?.email}
                        </p>
                    </div>
                </div>
                <span className={`${styles.badge} ${styles[shiftStatusBadgeClass(shift.status)]}`}>{shiftStatusLabel(shift.status)}</span>
            </div>

            <div className={styles.sectionCard} style={{ maxWidth: "100%" }}>
                <div className={styles.sectionCardHeader}>
                    <h3>
                        <i className="pi pi-info-circle" />
                        Shift Summary
                    </h3>
                </div>
                <div className={styles.metaGrid}>
                    <div className={styles.metaBox}>
                        <div className={styles.metaLabel}>Clocked In</div>
                        <div className={styles.metaValue}>{new Date(shift.clockIn.time).toLocaleString()}</div>
                    </div>
                    <div className={styles.metaBox}>
                        <div className={styles.metaLabel}>Clock-In Location</div>
                        <div className={styles.metaValue}>
                            <LocationCell point={shift.clockIn} />
                        </div>
                    </div>
                    <div className={styles.metaBox}>
                        <div className={styles.metaLabel}>Clocked Out</div>
                        <div className={styles.metaValue}>{shift.clockOut?.time ? new Date(shift.clockOut.time).toLocaleString() : "Still on shift"}</div>
                    </div>
                    <div className={styles.metaBox}>
                        <div className={styles.metaLabel}>Clock-Out Location</div>
                        <div className={styles.metaValue}>{shift.clockOut ? <LocationCell point={shift.clockOut} /> : "-"}</div>
                    </div>
                    <div className={styles.metaBox}>
                        <div className={styles.metaLabel}>{shift.status === "active" ? "Elapsed" : "Total Hours"}</div>
                        <div className={styles.metaValue}>{formatHours(shift.status === "active" ? shift.elapsedHours : shift.totalHours)}</div>
                    </div>
                    <div className={styles.metaBox}>
                        <div className={styles.metaLabel}>Breaks Logged</div>
                        <div className={styles.metaValue}>
                            {shift.breaks?.length || 0} ({formatHours((shift.breaks || []).reduce((s, b) => s + (b.hours || 0), 0))})
                        </div>
                    </div>
                </div>
            </div>

            <div className={styles.sectionCard} style={{ maxWidth: "100%" }}>
                <div className={styles.sectionCardHeader}>
                    <h3>
                        <i className="pi pi-map" />
                        Clock-In / Clock-Out Map
                    </h3>
                </div>
                <ShiftMap markers={clockMarkers} showPath={clockMarkers.length > 1} emptyMessage="No clock-in location recorded" />
                <div className={styles.mapLegend}>
                    <span>
                        <i className={`${styles.legendDot} ${styles.legendDotIn}`} /> Clock In
                    </span>
                    {shift.clockOut && (
                        <span>
                            <i className={`${styles.legendDot} ${styles.legendDotOut}`} /> Clock Out
                        </span>
                    )}
                </div>
            </div>

            <div className={styles.sectionCard} style={{ maxWidth: "100%" }}>
                <div className={styles.sectionCardHeader}>
                    <h3>
                        <i className="pi pi-pause-circle" />
                        Breaks
                    </h3>
                </div>
                {shift.breaks?.length ? (
                    [...shift.breaks].reverse().map((b, idx) => (
                        <div className={styles.breakRow} key={idx}>
                            <strong>{formatHours(b.hours)}</strong>
                            <span>{b.note || "No note"}</span>
                            <span>{new Date(b.loggedAt).toLocaleString()}</span>
                        </div>
                    ))
                ) : (
                    <div className={styles.emptyBreaks}>No breaks logged for this shift.</div>
                )}
            </div>

            <div className={styles.sectionCard} style={{ maxWidth: "100%" }}>
                <div className={styles.sectionCardHeader}>
                    <h3>
                        <i className="pi pi-map-marker" />
                        Hourly Location Log ({shift.geoPings?.length || 0})
                    </h3>
                </div>
                <ShiftMap markers={pingMarkers} showPath emptyMessage="No hourly pings recorded yet" />
                <div style={{ height: "18px" }} />
                <ResponsiveView
                    value={shift.geoPings || []}
                    emptyMessage="No location pings recorded for this shift."
                    storageKey="shiftDetail.pings"
                    rows={10}
                    flush
                    renderCard={(row, i) => (
                        <DataCard key={`${row.time}-${i}`} title={`Ping ${i + 1}`} subtitle={geoTimeBody(row)} fields={[{ label: "Coordinates", value: geoPingBody(row) }]} />
                    )}
                >
                    <DataTable value={shift.geoPings || []} paginator rows={10} className="datatable-responsive custom-datatable" emptyMessage="No location pings recorded for this shift.">
                        <Column header="Time" body={geoTimeBody} />
                        <Column header="Coordinates" body={geoPingBody} />
                    </DataTable>
                </ResponsiveView>
            </div>
        </div>
    );
};

export default AdminShiftDetail;
